import { items, required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { PROPERTIES_QUERY } from 'features/properties/components/list/queries';
import useQuery from 'hooks/apollo/useQuery';
import { useMemo } from 'react';
import { noValidation, uniqueField } from 'utils/simple-validations';

export const PropertyContext = Object.freeze({
  FinancialProduct: 'FinancialProduct'
});

const propertyValidator = ({ propertyField, valueField, valueRequired }) =>
  shape({
    [propertyField]: required,
    [valueField]: valueRequired ? required : noValidation
  });

const propertiesValidator = ({ propertyField, valueField, distinctProperties, requiredValue }) =>
  items(
    shape({
      [propertyField]: [required, distinctProperties ? uniqueField : noValidation] |> stopOnFirstFailure,
      [valueField]: requiredValue ? required : noValidation
    })
  );

const defaultOptions = {
  propertyField: 'propertyId',
  valueField: 'propertyValue',
  distinctProperties: true,
  requiredValue: false,
  listValidator: true
};

const usePropertiesData = (context, options = {}) => {
  const internalOptions = useMemo(() => ({ ...defaultOptions, ...options }), [options]);

  // #region Data loading

  const { data: propertiesData } = useQuery(PROPERTIES_QUERY, { variables: { context } });

  const properties = useMemo(
    () =>
      (propertiesData?.properties || []).map(property => ({
        id: property.propertyId,
        name: property.propertyCode + ' / ' + property.propertyName,
        typeCode: property.propertyTypeCode
      })),
    [propertiesData]
  );

  // #endregion

  // #region Validation

  const { propertyField, valueField, distinctProperties, requiredValue, listValidator } = internalOptions;

  const validator = useMemo(
    () =>
      listValidator
        ? propertiesValidator({ propertyField, valueField, distinctProperties, requiredValue })
        : propertyValidator({ propertyField, valueField, requiredValue }),
    [distinctProperties, listValidator, propertyField, requiredValue, valueField]
  );

  const [validation, validate, resetValidation] = useDirtyFieldValidation(validator);

  // #endregion

  return { validation, validate, resetValidation, properties };
};

export default usePropertiesData;
